import React, {useEffect, useState} from 'react';

import {useDarkMode} from "../../contexts/darkModeContext";
import {
    Button,
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle, Field,
    Input, Label,
    Select, Switch,
} from "@headlessui/react";
import {
    ArrowDownIcon,
    ArrowPathIcon,
    ArrowUpIcon,
    ArrowUpOnSquareIcon,
    DocumentArrowDownIcon,
    DocumentArrowUpIcon,
    DocumentCurrencyDollarIcon,
    MagnifyingGlassIcon,
    PlusCircleIcon,
    PlusIcon,
    SquaresPlusIcon,
    TrashIcon,
    XCircleIcon
} from "@heroicons/react/24/outline";
import {db, storage} from "../../firebase/firebase";
import {useAuth} from "../../contexts/authContext";
import PricingCard from "./PricingCard";

import { utils as xlsxUtils, write as writeXlsx } from 'xlsx'
import { saveAs } from 'file-saver'
import Loading from "../Widgets/Loading";
import {collection, doc, getDocs, updateDoc} from "firebase/firestore";
import {getDownloadURL, ref, uploadBytes} from "firebase/storage";
import {categoryColors, classNames, numberWithCommas} from "../GLOBAL/GlobalFunctions";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {useNotification} from "../Notifications/NotificationProvider";
import UMIcons from "../Widgets/UMIcons";
import * as XLSX from "xlsx";
import ActionPillButton from "../Widgets/ActionPillButton";
import CustomButton from "../Widgets/CustomButton";

import { ReactComponent as SIDE } from "../../customIcons/SideBar.svg";

const Pricing = () => {

    const { darkMode } = useDarkMode();
    const { currentUser } = useAuth();
    const { triggerNotification } = useNotification();
    
    const [alertsOn] = useState(() => {
        const storedSettings = localStorage.getItem('alertsOn');
        return storedSettings ? JSON.parse(storedSettings).alertsOn : false; // Default to false if null
    });

    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    
    const [Fname, setFname] = useState("");
    
    const [sidebarExpanded, setSidebarExpanded] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            if (currentUser) {
                try {
                    const storedProducts = localStorage.getItem(`products_${currentUser.uid}`);
                    if (storedProducts) {
                        const storedData = JSON.parse(storedProducts);
                        setProducts(storedData);
                        setFilteredProducts(storedData);
                    } else {

                        const productsCollection = db.collection("Products");
                        const snapshot = await productsCollection.get();

                        if (!snapshot.empty) {
                            const productData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                            setProducts(productData);
                            setFilteredProducts(productData);
                            localStorage.setItem(`products_${currentUser.uid}`, JSON.stringify(productData));
                        } else {
                            console.log('Product documents not found');
                        }

                    }
                } catch (error) {
                    console.error('Error fetching products data:', error);
                }
            }
        };
        
        const fetchUserName = async () => {
            if (currentUser) {
                try {
                    // Check if user data is in localStorage
                    const storedUserData = localStorage.getItem(`userData_${currentUser.uid}`);

                    if (storedUserData) {
                        // Use the stored user data
                        const userData = JSON.parse(storedUserData);
                        setFname(userData.fname);
                    } else {
                        // Fetch user document from Firestore based on the current user's UID
                        const userDoc = await db.collection('Users').doc(currentUser.uid).get();
                        if (userDoc.exists) {
                            // Get the user's name from the document data
                            const userData = userDoc.data();
                            setFname(userData.fname);
                            
                            // Save user data to localStorage
                            localStorage.setItem(`userData_${currentUser.uid}`, JSON.stringify(userData));
                        } else {
                            console.log('User document not found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            }
        };
        
        fetchUserName();

        fetchProducts();
    }, [currentUser]);

    const [updating, setUpdating] = useState(false);

    const onUpdate = async () => {
        setUpdating(true);
        const startTime = Date.now();

        // Pending
        try {
            const productsCollection = db.collection("Products");
            const snapshot = await productsCollection.get();

            if (!snapshot.empty) {
                const productData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setProducts(productData);
                setFilteredProducts(productData);
                localStorage.setItem(`products_${currentUser.uid}`, JSON.stringify(productData));
            } else {
                console.log('Product documents not found');
            }

        } catch (error) {
            console.error("Error updating products", error);
            triggerNotification("pricingUpdateError");
        }

        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) /1000;
        setUpdating(false);
        
        triggerNotification("pricingUpdate");
        
        if (alertsOn) {
            alert(`Products Updated in ${timeElapsed} secs`);
        }
    };


    const [searchQuery, setSearchQuery] = useState("");

    const [adding, setAdding] = useState(false);

    // Function to toggle visibility
    const toggleVisibility = () => {
        setAdding(!adding);
    };

    // const filterProductsByCatagory = (catagory) => {
    //     const filtered = products.filter(product => catagory === '' || product.category === catagory);
    //     setFilteredProducts(filtered);
    // }

    const filterProductsByCatagory = (category) => {
        if (category === 'All' || category === '') {
            setFilteredProducts(products);
        } else {
            const filtered = products.filter(product => product.category === category);
            setFilteredProducts(filtered);
        }
    };
    
    const handleSearch = (query) => {
        setSearchQuery(query);
        const filtered = products.filter((product) =>
            product.name?.toLowerCase().includes(query.toLowerCase()) ||
            product.updatedBy?.toLowerCase().includes(query.toLowerCase()) ||
            product.prices?.some((price) => price.code?.toLowerCase().includes(query.toLowerCase())) ||
            product.prices?.some((price) => price.um?.toLowerCase().includes(query.toLowerCase()))
        );
        setFilteredProducts(filtered);
    }



    const [prices, setPrices] = useState([]);

    const handleAddPrice = () => {
        setPrices([...prices, { um: '', code: '', ne: '', se: '', ag: '', dist1: '', dist2: '' }]);
    };

    const handleRemovePrice = (index) => {
        const newLocations = prices.filter((_, i) => i !== index);
        setPrices(newLocations);
    };

    // Updated handleInputChange
    const handleInputChange = (index, e) => {
        const { name, value } = e.target;
        const newPrices = [...prices];
        newPrices[index][name] = value;
        setPrices(newPrices);
    };



    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [sds, setSds] = useState("");
    const [promotionalImageFile, setPromotionalImageFile] = useState(null);

    const [ph, setPh] = useState(0);
    const [weight, setWeight] = useState(0);
    
    const [AG, setAG] = useState(false);
    const [PLAIF, setPLAIF] = useState(false);
    
    const [BULK, setBulk] = useState(false);
    const [BULKCODE, setBulkCode] = useState("");
    
    // const [INT, setINT] = useState(false);
    // const [country, setCountry] = useState("");

    const onSubmitMain = async (e) => {
        e.preventDefault();

        const collectionRef = db.collection("Products");

        const parsedFormData = {

            // Sales Info
            name: name,
            category: category,
            ph: parseFloat(ph),
            weight: parseFloat(weight),
            sds: sds,
            
            AG: AG,
            PLAIF: PLAIF,
            
            BULK: BULK,
            BULKCODE: BULKCODE,

            updated: Date.now(),
            updatedBy: Fname,

            prices: prices,
        };

        try {
            const productDoc = await collectionRef.add(parsedFormData);

            if (category === 'Promotional Item' && promotionalImageFile) {
                const safeFileName = promotionalImageFile.name.replace(/[^a-zA-Z0-9._-]/g, '-');
                const imagePath = `productImages/promotional/${productDoc.id}/${Date.now()}-${safeFileName}`;
                const snapshot = await uploadBytes(ref(storage, imagePath), promotionalImageFile);
                const imageUrl = await getDownloadURL(snapshot.ref);
                await productDoc.update({
                    promotionalImageUrl: imageUrl,
                    promotionalImagePath: imagePath,
                });
            }
            
            if (alertsOn) {
                alert(`Product Added to the Cloud`); // Optional: alert for user feedback
            }
            
            // Update the productUpdated field in all documents in the Users collection
            const usersCollectionRef = collection(db, "Users");
            const usersSnapshot = await getDocs(usersCollectionRef);

            const updatePromises = usersSnapshot.docs.map((userDoc) => {
                const userDocRef = doc(db, "Users", userDoc.id);
                return updateDoc(userDocRef, { productUpdated: true });
            });
            
            await Promise.all(updatePromises); // Wait for all updates to complete
            console.log("Updated productUpdated field for all users!");

            await onUpdate();
            triggerNotification("pricingAdded");

            setName("");
            setCategory("");
            setPh(0);
            setWeight(0);
            setPromotionalImageFile(null);

            setPrices([]);

            // onUpdate();

        } catch (error) {
            console.error('Error registering product:', error.message);
            triggerNotification("pricingAddedError");
            // setIsRegistered(false);
        }
    };
    

    const bgColor = categoryColors.find(c => c.category === category)?.color || '';
    

    const downloadAsExcel = () => {
        const columnHeaders = [
            'Code',
            'Item Name',
            'U/M',
            'Unit Price 1 (NE)',
            'Unit Price 1 (SE)',
            'Unit Price 3 (AG)',
            'Unit Price 4 (DIST 1)',
            'Unit Price 5 (DIST 2)',
        ];

        // Map through products to extract product details
        const worksheetData = [
            columnHeaders,
            ...filteredProducts.flatMap(order =>
                order.prices.map(price => [
                    price.code || '',
                    order.name || '',
                    price.um || '',
                    numberWithCommas(price.ne) || 0,
                    numberWithCommas(price.se) || 0,
                    numberWithCommas(price.ag) || 0,
                    numberWithCommas(price.dist1) || 0,
                    numberWithCommas(price.dist2) || 0
                ])
            )
        ];

        // Generate Excel worksheet
        const worksheet = xlsxUtils.aoa_to_sheet(worksheetData);

        // Set custom column widths (double the default width)
        worksheet['!cols'] = [
            { wch: 20 }, // Item
            { wch: 40 }, // Item Description
            { wch: 10 }, // U/M
            { wch: 20 }, // Unit Price 1 (NE)
            { wch: 20 }, // Unit Price 1 (SE)
            { wch: 20 }, // Unit Price 3 (AG)
            { wch: 20 }, // Unit Price 4 (DIST 1)
            { wch: 20 }, // Unit Price 5 (DIST 2)
        ];

        // Create workbook and append worksheet
        const workbook = xlsxUtils.book_new();
        xlsxUtils.book_append_sheet(workbook, worksheet, 'Product Pricing');

        // Get current date in yyyymmdd format
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const dd = String(today.getDate()).padStart(2, '0');
        const formattedDate = `${yyyy}${mm}${dd}`;

        // Convert workbook to buffer and create Blob
        const excelBuffer = writeXlsx(workbook, { bookType: 'xlsx', type: 'array' });
        const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        // Save the Excel file with the dynamic filename
        saveAs(excelBlob, `ItemPricingExport${formattedDate}.xlsx`);
    };
    
    // eslint-disable-next-line
    const downloadAsExcelSHORT = () => {
        const columnHeaders = ['Product Name',];
        const worksheetData = [columnHeaders, ...products.map(product => [product.name])];
        const worksheet = xlsxUtils.aoa_to_sheet(worksheetData);
        const workbook = xlsxUtils.book_new();
        xlsxUtils.book_append_sheet(workbook, worksheet, 'Active Products');
        const excelBuffer = writeXlsx(workbook, { bookType: 'xlsx', type: 'array' });
        const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(excelBlob, 'ActiveProducts.xlsx');
    };

    
    const downloadJSON = () => {
        const dataStr = JSON.stringify(products, null, 2); // Convert data to JSON string
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `products.json`; // Use a unique name for each file
        link.click();

        URL.revokeObjectURL(url); // Clean up the URL object
    };


    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    
    const [secondaryNavigation, setSecondaryNavigation] = useState([
      // { name: 'Dashboard', href: '#', current: false },
      { name: 'All' },
      { name: 'Biostimulants' },
      { name: 'Liquid Fertilizer' },
      { name: 'Dry Fertilizer' },
      { name: 'Secondary Nutrients' },
      { name: 'Micronutrients' },
      { name: 'Penetrants & Water Holding Agents' },
      { name: 'A-Plus® Solubilizing Surfactants' },
      { name: 'Turfgrass Pigment' },
      { name: 'Water Treatments' },
      { name: 'Soil Amendments' },
      { name: 'Fungicides' },
      { name: 'Equipment' },
      { name: 'Test' },
      { name: 'Promotional Item' },
    ]);
    
    
    const categoryOrder = secondaryNavigation.map(item => item.name);
    const sortedProducts = [...filteredProducts].sort((a, b) => {
      const indexA = categoryOrder.indexOf(a.category);
      const indexB = categoryOrder.indexOf(b.category);

      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });
    const currentProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);

    
    
    // const currentProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);
    

    
    
    function countPricesOverZero(products, regionKey) {
      let count = 0;
    
      products.forEach(product => {
        if (Array.isArray(product.prices)) {
          product.prices.forEach(priceObj => {
            if (priceObj[regionKey] > 0) {
              count++;
            }
          });
        }
      });
    
      return count;
    }
    
   
    const stats = [
      { name: `NE Products`, value: countPricesOverZero(filteredProducts, "ne") , unit: `line items` },
      { name: 'SE Products', value: countPricesOverZero(filteredProducts, "se"), unit: `line items` },
      { name: 'AG Products', value: countPricesOverZero(filteredProducts, "ag"), unit: `line items` },
      { name: 'Dist1 + Dist2 Products', value: `${countPricesOverZero(filteredProducts, "dist1")} - ${countPricesOverZero(filteredProducts, "dist2")}`, unit: `line items` },
    ]
    
    
    const [collapse, setCollapse] = useState(false);
    
    const toggleCollapse = () =>{
        setCollapse(!collapse);
    }
    
    
    
    const [AscDesc, setAscDesc] = useState(true);
    const [priceAscDesc, setPriceAscDesc] = useState(true);

    const toggleLIAscDesc = () => {
        // Toggle AscDesc and sort the orders array
        setAscDesc((prev) => !prev);

        const sortedOrders = [...filteredProducts].sort((a, b) => {
            return AscDesc
                ? a.prices.length - b.prices.length  // Ascending
                : b.prices.length - a.prices.length; // Descending
        });
        setFilteredProducts(sortedOrders);
        
    };
    
    const togglePriceAscDesc = () => {
        // Determine next sort direction
        const nextSortAsc = !priceAscDesc;
    
        // Extract valid numbers from all price entries in the prices array
        const extractPrices = (product) => {
            if (!Array.isArray(product.prices)) return [];
    
            return product.prices.flatMap(priceObj => {
                const { ag, ne, se, dist1, dist2 } = priceObj || {};
                return [ag, ne, se, dist1, dist2]
                    .map(p => parseFloat(p))
                    .filter(p => !isNaN(p)); // Keep only valid numbers
            });
        };
    
        // Choose min or max based on the sort direction
        const getPriceValue = (product) => {
            const prices = extractPrices(product);
            if (!prices.length) return nextSortAsc ? Infinity : -Infinity;
            return nextSortAsc ? Math.min(...prices) : Math.max(...prices);
        };
    
        // Sort all products based on extracted price values
        const sorted = [...products].sort((a, b) => {
            const priceA = getPriceValue(a);
            const priceB = getPriceValue(b);
            return priceB - priceA;
        });
    
        setFilteredProducts(sorted);
        setPriceAscDesc(nextSortAsc);
    };
    
    function getTotalPriceEntries(filteredProducts) {
      return filteredProducts.reduce((total, product) => {
        return total + (Array.isArray(product.prices) ? product.prices.length : 0);
      }, 0);
    }
    
    const [upload, setUpload] = useState(false);
    
    
    const [uploadedData, setUploadedData] = useState([]);
    const [comparisonResults, setComparisonResults] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [ready, setReady] = useState(false);
    
    

    
// Utility: clean Excel values and always return string
const cleanValue = (val, existingVal = "") => {
  if (val === undefined || val === null) {
    // Excel cell missing → keep current value
    return String(existingVal ?? "");
  }

  // Strip $, commas, whitespace
  const cleaned = String(val).replace(/[$,]/g, "").trim();

  // If cleaned is empty string or dash → keep current
  if (cleaned === "" || cleaned === "-") {
    return String(existingVal ?? "");
  }

  // ✅ Convert number-like strings to plain string
  return cleaned;
};

// Step 1: Upload Excel
const handleFileUpload = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (evt) => {
    const bstr = evt.target.result;
    const workbook = XLSX.read(bstr, { type: "binary" });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

    const cleaned = data.map((row) => {
      // Trim all keys to handle spaces in header names
      const normalizedRow = {};
      Object.keys(row).forEach((key) => {
        normalizedRow[key.trim()] = row[key];
      });

      return {
        code: String(normalizedRow["Item"] || "").trim(),
        um: String(normalizedRow["U/M"] || "").trim(),
        ne: String(normalizedRow["NE Price"] || "").trim(),
        se: String(normalizedRow["SE Price"] || "").trim(),
        ag: String(normalizedRow["AG Price"] || "").trim(),
        dist1: String(normalizedRow["Dist-1"] || "").trim(),
        dist2: String(normalizedRow["Dist-2"] || "").trim(),
      };
    });

    console.log("✅ Uploaded data:", cleaned);
    setUploadedData(cleaned);
    setProgress({ done: 0, total: cleaned.length });
    setReady(true);
  };
  reader.readAsBinaryString(file);
};

// Step 2: Compare and preview changes
const handleCompareAndUpload = () => {
  const results = [];

  uploadedData.forEach((row) => {
    const product = products.find((p) =>
    p.prices.some((p2) =>
      p2.code.trim() === row.code.trim() &&
      String(p2.um).trim() === String(row.um).trim()
    )
    );

    if (!product) return;

   const existingPrice = product.prices.find(
      (p2) =>
        p2.code.trim() === row.code.trim() &&
        String(p2.um).trim() === String(row.um).trim()
    );

    if (!existingPrice) return;

    const changes = {};

    ["ne", "se", "ag", "dist1", "dist2"].forEach((key) => {
      const newValue = String(row[key] || "").trim();

      // ✅ IGNORE "-" and empty values
      if (newValue === "-" || newValue === "") return;

      if (String(existingPrice[key] ?? "").trim() !== newValue.trim()) {
        changes[key] = {
          old: existingPrice[key],
          new: newValue,
        };
      }
    });

    if (Object.keys(changes).length > 0) {
      results.push({
          productId: product.id,
          productName: product.name,
          code: row.code,
          um: row.um,
          changes,
        });
    }
  });

  console.log("🔍 Comparison Results:", results);
  setComparisonResults(results);
  setShowPreview(true);
};

// Step 3: Confirm and upload changes
const handleConfirmUpload = async () => {
  let processedRows = 0;
  const totalRows = comparisonResults.length;

  setProgress({ done: 0, total: totalRows });

  for (const item of comparisonResults) {
    const product = products.find((p) => p.id === item.productId);
    if (!product) continue;

    const updatedPrices = [...product.prices];

    const idx = updatedPrices.findIndex(
      (p) =>
        p.code.trim() === item.code.trim() &&
        String(p.um).trim() === String(item.um).trim()
    );

    if (idx === -1) continue;

    const updated = { ...updatedPrices[idx] };

    Object.keys(item.changes).forEach((key) => {
      const rawValue = item.changes[key].new;

      // Preserve numeric values properly
      const newValue =
        rawValue === undefined || rawValue === null
          ? ""
          : String(rawValue).trim();

      // ✅ DOUBLE SAFETY: ignore "-" or empty
      if (newValue === "-" || newValue === "") return;

      // Store as number when possible
      const parsedNumber = parseFloat(newValue);

      updated[key] = !isNaN(parsedNumber)
        ? parsedNumber
        : newValue;
    });

    updatedPrices[idx] = updated;
    console.log("✅ Updated price object:", updatedPrices[idx]);

    console.log("📝 Updating product:", {
      productId: item.productId,
      updatedPrices,
      updated,
      changes: item.changes,
    });
    const ref = doc(db, "Products", item.productId);
    await updateDoc(ref, {
      prices: updatedPrices,
      updatedBy: "AUTO-SYSTEM",
      updatedAt: new Date(),
    });

    processedRows++;
    setProgress({ done: processedRows, total: totalRows });
  }

  alert("All selected changes uploaded successfully!");
  setShowPreview(false);
  setComparisonResults([]);
};
    
    return (
        <div className={`${darkMode ? 'text-white' : 'text-black'} flex h-[calc(100dvh-3.5rem)] w-full transition-all duration-500 ease-in-out`}>

            {updating && (
                <Loading/>
            )}
            
            
            
            {/*Redesign*/}
            <div className={`animate-fadeIn z-20 ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} w-full`}>
        
                <main>
                    
                    {/*Header*/}
                    <header>
                        
                        {/* Secondary navigation */}
                        <nav className={`hidden flex items-center justify-between overflow-x-auto border-b ${darkMode ? 'border-neutral-700' : 'border-gray-300'} py-2`}>
                            <div
                                role="list"
                                className={`flex scrollbar-hide flex-none divide-x-2 ${darkMode ? 'divide-neutral-700' : 'divide-gray-300'} px-4 text-sm/6 font-semibold text-gray-500`}
                            >
                                {secondaryNavigation.map((item) => (
                                    <div
                                      key={item.name}
                                      onClick={() => {
                                        // setStatus(item.href);
                                        filterProductsByCatagory(item.name);
                                        setSecondaryNavigation((nav) =>
                                          nav.map((navItem) => ({
                                            ...navItem,
                                            current: navItem.name === item.name,
                                          }))
                                        );
                                      }}
                                      className="cursor-pointer w-fit px-2"
                                    >
                                      <div className={`${item.current ? 'text-green-600 underline' : ''} line-clamp-1`}>
                                        {item.name}
                                      </div>
                                    </div>
                                  ))}
                            </div>
                            
                            <Button
                                onClick={toggleCollapse}
                                className={`z-50 mx-4 absolute right-0`}
                            >
                                <ChevronDownIcon className={`${collapse ? 'rotate-180' : ''} stroke-2 transition-all duration-1000 size-6 text-green-600`}/>
                            </Button>
                            
                        </nav>
                        
                        {/* Heading */}
                        <div className={`${collapse ? 'hidden' : ''} flex flex-col items-start justify-between gap-x-4 ${darkMode ? ' bg-gray-700/10' : 'bg-neutral-300/50'} px-4 py-2 sm:flex-row sm:items-center`}>
                            <div>
                                <div className="flex items-center gap-x-2">
                                    
                                    <ActionPillButton
                                        onClick={onUpdate}
                                        icon={ArrowPathIcon}
                                        color={`green`}
                                        label={`Sync`}
                                    />
                                    
                                    {/*<Button onClick={onUpdate} className={`flex-none rounded-full p-1 hover:rotate-90 transition-all duration-1000 hover:scale-110`}>*/}
                                    {/*    <ArrowPathIcon className={`size-4 text-green-600`}/>*/}
                                    {/*</Button>*/}
                                    
                                    <h1 className="flex items-center gap-x-1.5 text-sm text-base/7">
                                        <span className="font-semibold">Product Pricing</span>
                                        <span className="text-gray-500">|</span>
                                        <span className="font-semibold">{filteredProducts.length} <span className={`text-gray-500 text-xs`}>Products</span></span>
                                        <span className="text-gray-500 text-xs">|</span>
                                        <span className="font-semibold">{getTotalPriceEntries(filteredProducts)} <span className={`text-gray-500 text-xs`}>line items</span> </span>
                                    
                                    </h1>
                                </div>
                                <p className="mt-2 text-xs/6 text-gray-500">Pricing that is live for all sales reps & distributors in the system</p>
                            </div>
                            
                            {/*Buttons*/}
                            <div className={`space-x-2 flex items-center`}>
                                
                                <ActionPillButton
                                    onClick={() => setUpload(!upload)}
                                    icon={DocumentArrowUpIcon}
                                    color={`green`}
                                    label={`Upload & Update`}
                                />
                                
                                <ActionPillButton
                                    onClick={downloadJSON}
                                    icon={DocumentArrowDownIcon}
                                    color={`blue`}
                                    label={`JSON`}
                                />
                                
                                <ActionPillButton
                                    onClick={downloadAsExcel}
                                    icon={DocumentArrowDownIcon}
                                    color={`blue`}
                                    label={`Excel`}
                                />
                                
                                <CustomButton
                                    onClick={() => setAdding(true)}
                                    icon={PlusCircleIcon}
                                    color={`green`}
                                    text={`Add Product`}
                                />
                                
                            </div>
                        </div>
                        
                        {/* Stats */}
                        <div className={`${collapse ? 'hidden' : ''} grid grid-cols-1 ${darkMode ? ' bg-gray-700/10' : 'bg-neutral-300/50'} sm:grid-cols-2 lg:grid-cols-4`}>
                            
                            {stats.map((stat, statIdx) => {
                                
                            
                                return (
                                    <div
                                        key={stat.name}
                                        className={classNames(
                                            statIdx % 2 === 1 ? 'sm:border-l' : statIdx === 2 ? 'lg:border-l' : '',
                                            `border-t ${darkMode ? 'border-neutral-700' : 'border-gray-300'} px-4 py-1`
                                        )}
                                    >
                                        <div className={`px-2 py-1 rounded-lg`}>
                                            <p className="text-sm/6 font-bold text-gray-500">{stat.name}</p>
                                            <p className="flex items-baseline gap-x-2">
                                                <span className="text-2xl font-semibold tracking-tight">{stat.value}</span>
                                                {stat.unit ? (
                                                    <span
                                                        className={`text-sm px-1 rounded-lg font-bold text-green-600`}
                                                    >
                                                        {stat.unit}
                                                    </span>
                                                ) : null}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        
                        
                        </div>
                    
                    </header>
                    
                    {/* Activity list */}
                    <div className="grid grid-cols-8 border-t border-white/10">
                        
                        {/*Filter Side Bar*/}
                        <div className={`${sidebarExpanded ? 'col-span-1' : 'hidden'} duration-500 border-r border-white/10 flex flex-col h-[calc(100dvh-14rem)]`}>
                            
                            {/*Categories Header*/}
                            <div className={`bg-gray-700/10 flex items-center justify-between border-b border-white/10 p-1.5`}>
                                <span className={`text-sm`}>Categories</span>
                                <span className={`text-gray-500 text-xs`}>{secondaryNavigation.length}</span>
                            </div>
                            
                            {/*Category*/}
                            <div
                                role="list"
                                className={`flex flex-col scrollbar-hide flex-none ${darkMode ? 'divide-neutral-700' : 'divide-gray-300'} text-xs gap-1.5 py-1.5 font-semibold text-gray-500`}
                            >
                                {secondaryNavigation.map((item) => (
                                    <div
                                        key={item.name}
                                        onClick={() => {
                                            // setStatus(item.href);
                                            filterProductsByCatagory(item.name);
                                            setSecondaryNavigation((nav) =>
                                                nav.map((navItem) => ({
                                                    ...navItem,
                                                    current: navItem.name === item.name,
                                                }))
                                            );
                                        }}
                                        className="cursor-pointer w-fit px-2 hover:text-green-600 hover:tracking-wide"
                                    >
                                        <div className={`${item.current ? 'text-green-600 border-l-2 pl-1.5 border-green-600' : ''} line-clamp-1`}>
                                            {item.name}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/*Filters Header*/}
                            <div className={`bg-gray-700/10 flex items-center justify-between border-y border-white/10 p-1.5`}>
                                <span className={`text-sm`}>Filters</span>
                            </div>
                            
                            {/*Filters*/}
                            <div className={`flex flex-col space-y-1.5 py-1.5`}>
                                <Button
                                    onClick={togglePriceAscDesc}
                                    className={`relative gap-x-2 inline-flex items-center justify-between px-2 py-1 text-xs font-semibold hover:text-green-600 hover:bg-gray-700/10 duration-500 focus:z-10`}
                                >
                                    Price
                                    <ArrowUpIcon className={`stroke-2 size-4 text-gray-500 ${priceAscDesc ? 'rotate-180' : 'rotate-0'} duration-500`}/>
                                </Button>
                                
                                <Button
                                    onClick={toggleLIAscDesc}
                                    className={`relative gap-x-2 inline-flex justify-between items-center px-2 py-1 text-xs font-semibold hover:text-green-600 hover:bg-gray-700/10 duration-500 focus:z-10`}
                                >
                                    Line Items
                                    <ArrowUpIcon className={`stroke-2 size-4 text-gray-500 ${AscDesc ? 'rotate-180' : 'rotate-0'} duration-500`}/>
                                </Button>
                            </div>
                        
                        </div>
                        
                        {/*Search Bar and Products*/}
                        <div className={`${sidebarExpanded ? 'col-span-7' : 'col-span-full'} duration-500`}>
                            
                            {/*Search bar row*/}
                            <div className={`px-2 grid grid-cols-3 w-full h-fit items-center ${darkMode ? 'text-white' : 'text-black'}`}>
                                
                                {/*Search Bar*/}
                                <div className="col-span-2 gap-x-3 flex items-center h-fit text-sm">
                                    
                                    <Button onClick={() => setSidebarExpanded(!sidebarExpanded)} className={`hover:text-green-600 text-gray-500`}>
                                        <SIDE className={`size-4`}/>
                                    </Button>
                                    
                                    <MagnifyingGlassIcon className="size-5 text-gray-500"/>
                                    
                                    <Input
                                        className={`${darkMode ? 'text-white bg-darkMainColor' : 'text-black bg-white'} placeholder:tracking-widest placeholder:text-xs rounded-md py-1.5 w-full focus:outline-none`}
                                        placeholder={`Search ${products.length} Products By...   Name, Product Code, UM, Updating User`}
                                        value={searchQuery}
                                        onChange={(e) => handleSearch(e.target.value)}
                                    />
                                
                                </div>
                                
                            </div>
                            
                            {/*Products MAP*/}
                            <div className={`overflow-y-auto  ${collapse ? 'h-[calc(100dvh-10rem)]' : 'h-[calc(100dvh-16rem)]'} border-t scrollbar-hide ${darkMode ? 'text-white bg-darkMainColor border-neutral-700' : 'text-black bg-white'} rounded-b-lg scrollbar-hide`}>
                                
                                <div className={`w-full h-full`}>
                                    
                                    {filteredProducts.length === 0 ? (
                                        <div className="w-full h-full flex justify-center items-center">
                                            <div className={`flex items-center border-2 border-dashed ${darkMode ? "text-white border-borderColor" : "text-black bg-neutral-50"} w-fit px-24 py-6 h-fit mr-auto ml-auto justify-center rounded-lg hover:scale-105`}>
                                                <div className="text-center flex items-center flex-col">
                                                    <DocumentCurrencyDollarIcon className={`size-12 stroke-1 text-white`} />
                                                    <h3 className="mt-2 text-sm font-semibold">No Products</h3>
                                                    <p className="mt-1 text-sm">Get started by creating a new product.</p>
                                                    <div className="mt-6">
                                                        <Button
                                                            type="button"
                                                            onClick={toggleVisibility}
                                                            className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-50"
                                                        >
                                                            <PlusIcon aria-hidden="true" className="-ml-0.5 mr-1.5 size-5" />
                                                            New Product
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 auto-rows-auto">
                                                {currentProducts.map((product, index) => (
                                                    <PricingCard
                                                        index={index}
                                                        key={product.id}
                                                        product={product}
                                                        user={Fname}
                                                    />
                                                ))}
                                                
                                            </div>
                                            <nav
                                                aria-label="Pagination"
                                                className={`flex items-center justify-between px-4 py-3 sm:px-6`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <p className="text-sm">
                                                        Showing <span className="font-medium">{startIndex + 1}</span> to <span
                                                        className="font-medium">{Math.min(startIndex + itemsPerPage, filteredProducts.length)}</span> of{' '}
                                                        <span className="font-medium">{filteredProducts.length}</span> results
                                                    </p>
                                                    <select
                                                        value={itemsPerPage}
                                                        onChange={(e) => {
                                                            setItemsPerPage(Number(e.target.value));
                                                            setCurrentPage(1);
                                                        }}
                                                        className={`border rounded-lg p-1 ${darkMode ? "bg-darkMainColor border-neutral-700" : ""}`}
                                                    >
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                        <option value={75}>75</option>
                                                        <option value={100}>100</option>
                                                    </select>
                                                </div>
                                                <div className="flex flex-1 justify-between sm:justify-end">
                                                    <Button
                                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                        disabled={currentPage === 1}
                                                        className={`px-3 py-1 h-fit rounded-md ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "bg-green-600 text-white"}`}
                                                    >
                                                        Previous
                                                    </Button>
                                                    <Button
                                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                        disabled={currentPage === totalPages}
                                                        className={`px-3 py-1 h-fit ml-3 rounded-md ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "bg-green-600 text-white"}`}
                                                    >
                                                        Next
                                                    </Button>
                                                </div>
                                            </nav>
                                        </>
                                    )}
                                
                                </div>
                                
                              
                            </div>
                            
                        </div>
                        
                    </div>
                    
                </main>
            
            </div>
            
            
            {/*Add New Product*/}
            <Dialog open={adding} onClose={setAdding} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />
                
                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className={`relative transform overflow-hidden rounded-lg ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-7xl ${BULK ? 'h-[42rem]' : 'h-[38.5rem]'} sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                        >
                            {/*Body*/}
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-green-600/15 sm:mx-0 sm:size-10">
                                    <DocumentCurrencyDollarIcon aria-hidden="true" className="size-6 text-green-600"/>
                                </div>
                                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                    <DialogTitle as="h3" className="text-base flex items-center font-semibold">
                                        Add a New Product
                                    </DialogTitle>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            When adding in a new product you have to add in the Name and Category for the system to recognize it. Also to add in pricing and codes you must below add in a "Product Price" which will allow you to select U/M add in the product code and the 5 pricing types.
                                        </p>
                                    </div>
                                    
                                    
                                     {/* Modal body */}
                                    <form className="mt-5 grid grid-cols-4 gap-x-4 gap-y-4"
                                          onSubmit={onSubmitMain}
                                          method="POST">
            
                                            {/*Product Name*/}
                                            <div className={`relative col-span-2`}>
                                                <label
                                                    className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                >
                                                    Product Name
                                                    <div className={`ml-1`}>
                                                        <div className={`flex-none rounded-full p-1 ${name ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
                                                            <div className={`size-2 rounded-full ${name ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}`}/>
                                                        </div>
                                                    </div>
                                                </label>
                                                <Input
                                                    type="text"
                                                    required
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    placeholder={`Type Product Name`}
                                                    className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                />
                                            </div>
                                            
                                            {/*Category*/}
                                            <div className={`relative col-span-2`}>
                                                <label
                                                    className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                >
                                                    Product Category
                                                    <div className={`ml-1`}>
                                                        <div className={`flex-none rounded-full p-1 ${category ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
                                                            <div className={`size-2 rounded-full ${category ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}`}/>
                                                        </div>
                                                    </div>
                                                </label>
                                                <Select
                                                    type="text"
                                                    required
                                                    value={category}
                                                    onChange={(e) => setCategory(e.target.value)}
                                                    className={`block w-full rounded-md ${darkMode ? `${bgColor} bg-darkMainColor text-white outline-neutral-700 outline-2` : `${bgColor} bg-white text-gray-900 outline-gray-300`} px-2 py-2.5 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                >
                                                    <option value="">Select Category...</option>
                                                    <option value="Biostimulants">Biostimulants</option>
                                                    <option value="Liquid Fertilizer">Liquid Fertilizers</option>
                                                    <option value="Dry Fertilizer">Dry Fertilizers</option>
                                                    <option value="Secondary Nutrients">Secondary Nutrients</option>
                                                    <option value="Micronutrients">Micronutrients</option>
                                                    <option value="Penetrants & Water Holding Agents">Penetrants & Water Holding Agents</option>
                                                    <option value="A-Plus® Solubilizing Surfactants">A-Plus® Solubilizing Surfactants</option>
                                                    <option value="Turfgrass Pigment">Turfgrass Pigment</option>
                                                    <option value="Water Treatments">Water Treatments</option>
                                                    <option value="Soil Amendments">Soil Amendments</option>
                                                    <option value="Fungicides">Fungicides</option>
                                                    <option value="Equipment">Equipment</option>
                                                    <option value="Test">Test</option>
                                                    <option value="Seed">Seed</option>
                                                    <option value="Promotional Item">Promotional Item</option>
                                                    <option value="MISC">Misc.</option>
                                                </Select>
                                            </div>

                                            {category === 'Promotional Item' && (
                                                <div className="relative col-span-4">
                                                    <label className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}>
                                                        Promotional Product Image
                                                    </label>
                                                    <Input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => setPromotionalImageFile(e.target.files?.[0] || null)}
                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-2 text-sm outline outline-1 -outline-offset-1 file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-white`}
                                                    />
                                                    <div className="mt-1 text-xs text-gray-500">
                                                        {promotionalImageFile ? promotionalImageFile.name : 'Optional. You can also upload or replace it after creating the product.'}
                                                    </div>
                                                </div>
                                            )}
                                            
                                        
                                            {/*Product PH*/}
                                            <div className={`relative col-span-1`}>
                                                <label
                                                    className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                >
                                                    Product PH
                                                </label>
                                                <Input
                                                    type="number"
                                                    value={ph}
                                                    onChange={(e) => setPh(e.target.value)}
                                                    placeholder={`Type Product PH...`}
                                                    className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                />
                                            </div>
                                            
                                            {/*Product Weight*/}
                                            <div className={`relative col-span-1`}>
                                                <label
                                                    className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                >
                                                    Product Weight
                                                </label>
                                                <Input
                                                    type="number"
                                                    value={weight}
                                                    onChange={(e) => setWeight(e.target.value)}
                                                    placeholder={`Type Product Weight...`}
                                                    className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                />
                                            </div>
                                        
                                            {/*Product SDS*/}
                                            <div className={`relative col-span-1`}>
                                                <label
                                                    className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                >
                                                    SDS
                                                </label>
                                                <Input
                                                    type="string"
                                                    value={sds}
                                                    onChange={(e) => setSds(e.target.value)}
                                                    placeholder={`Type Product SDS...`}
                                                    className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                />
                                            </div>
                                        
                                            {/*SDS Label*/}
                                            <div className={`relative col-span-1`}>
                                                <div className={`text-xs text-gray-500`}>SDS is the unique identifier code so search the system for IMG's SDS's and Sell Sheets</div>
                                            </div>
            
            
                                            {/*AG*/}
                                            <div className={`relative col-span-2`}>
                                                {/*Automatic Updates*/}
                                                <Field className="flex items-center w-full col-span-2">
                                                    <Switch
                                                        checked={AG}
                                                        onChange={setAG}
                                                        className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                    >
                                                        <span className="sr-only">Use setting</span>
                                                        <span aria-hidden="true"
                                                              className="pointer-events-none absolute size-full rounded-md"/>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`pointer-events-none absolute mx-auto h-4 w-9 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                        />
                                                        <span
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute left-0 inline-block size-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                        />
                                                    </Switch>
                                                    <Label as="span" className="ml-3 text-sm w-full">
                                                        <span className="font-medium">AG only Product</span>{' '}
                                                        <span className="text-gray-500 text-xs">(Products with -AG at end of Code)</span>
                                                    </Label>
                                                </Field>
                                            </div>
                                            
                                            {/*BULK*/}
                                            <div className={`relative col-span-2`}>
                                                {/*Automatic Updates*/}
                                                <Field className="flex items-center w-full col-span-2">
                                                    <Switch
                                                        checked={BULK}
                                                        onChange={setBulk}
                                                        className="group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent"
                                                    >
                                                        <span className="sr-only">Use setting</span>
                                                        <span aria-hidden="true"
                                                              className="pointer-events-none absolute size-full rounded-md"/>
                                                        <span
                                                            aria-hidden="true"
                                                            className={`pointer-events-none absolute mx-auto h-4 w-9 rounded-full ${darkMode ? 'bg-neutral-700' : 'bg-gray-200'} transition-colors duration-200 ease-in-out group-data-[checked]:bg-green-600`}
                                                        />
                                                        <span
                                                            aria-hidden="true"
                                                            className="pointer-events-none absolute left-0 inline-block size-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
                                                        />
                                                    </Switch>
                                                    <Label as="span" className="ml-3 text-sm w-full">
                                                        <span className="font-medium">BULK Application Product</span>{' '}
                                                        <span className="text-gray-500 text-xs">(Product PFC will Spread)</span>
                                                    </Label>
                                                </Field>
                                            </div>
                                        
                                            {/*Product BULK CODE*/}
                                            <div className={`relative col-span-full ${BULK ? '' : 'hidden'}`}>
                                                <label
                                                    className={`absolute flex -top-2 left-2 rounded-lg ${darkMode ? 'bg-darkMainColor' : 'bg-white text-gray-900'} px-1 text-xs font-medium`}
                                                >
                                                    Bulk Code
                                                </label>
                                                <Input
                                                    type="text"
                                                    value={BULKCODE}
                                                    onChange={(e) => setBulkCode(e.target.value)}
                                                    placeholder={`Type Product Bulk Code...`}
                                                    className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white outline-neutral-700 outline-2' : 'bg-white text-gray-900 outline-gray-300'} px-2 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                />
                                            </div>
                                        
                                        
                                            {/*Prices Table*/}
                                            <div className={`col-span-full w-full flex flex-col border-2 ${darkMode ? `bg-darkMainColor border-neutral-700` : `bg-white`} rounded-lg`}>
                    
                                                <div className={`w-full relative grid grid-cols-7 py-0.5 gap-x-2 text-center items-center text-gray-500 text-sm border-b-2 bg-gray-500/10 rounded-t-md ${darkMode ? 'border-neutral-700' : ''}`}>
                                                    <div className={`col-span-1 flex items-center justify-center gap-x-3`}>{BULK ? 'RATE' : 'UM'}
                                                        <Button className={`text-green-600 hover:scale-105 stroke-2 hover:animate-pulse`}
                                                            onClick={handleAddPrice}>
                                                            <PlusCircleIcon className="size-5"/>
                                                        </Button>
                                                    </div>
                                                    <div className={`col-span-1`}>Code</div>
                                                    <div className={`col-span-1`}>NE</div>
                                                    <div className={`col-span-1`}>SE</div>
                                                    <div className={`col-span-1`}>AG</div>
                                                    <div className={`col-span-1`}>DIST1</div>
                                                    <div className={`col-span-1`}>DIST2</div>
                                                    <div className={`absolute left-1 flex items-center gap-x-1`}>
                                                        <span className={`${darkMode ? 'text-white' : 'text-black'}`}>[{prices.length}]</span>
                                                    </div>
                                                </div>
                                
                                                <div className={`h-56 rounded-b-md overflow-y-auto scrollbar-hide`}>
                                                    {prices.length === 0 ? (
                                                        // Display custom dialog if sprays array is empty
                                                        <div className="flex w-full h-full justify-center items-center py-5">
                                                            
                                                            <div className={`flex items-center border-2 border-dashed ${darkMode ? 'text-white border-borderColor' : 'text-black bg-neutral-50'} w-fit px-24 h-fit mr-auto ml-auto justify-center rounded-lg hover:scale-105`}>
                                                                <div className="p-2 rounded-md shadow-gray-900 flex items-center justify-center flex-col m-2">
                                                                    <SquaresPlusIcon className={`w-6 stroke-1 mr-3`}/>
                                                                    <p className="text-md line-clamp-1 tracking-widest flex">No Pricing Available</p>
                                                                </div>
                                                            </div>
                                                        
                                                        </div>
                                                    
                                                    ) : (
                                                        // Display SprayCard components if sprays array is not empty
                                                        <div className={``}>
                                                            {prices.map((price, index) => {
                                                                return (
                                                                    
                                                                    
                                                                    <div key={index} className={`animate-bounceIn border-b-2 ${darkMode ? 'border-neutral-700' : ''}`}>
                                                                        
                                                                        
                                                                        <div className="flex flex-row w-full items-center">
                                                                            
                                                                            {/*Fields Grid*/}
                                                                            <div className={`w-full grid grid-cols-7 divide-x-2 ${darkMode ? 'divide-neutral-700' : ''}`}>
                                                                                
                                                                                {/*U/M*/}
                                                                                <div className={`relative col-span-1 flex items-center`}>
                                                                                    
                                                                                    <UMIcons selectedVolume={price.um} size={'size-6'} color={'text-gray-500'}/>
                                                                                    
                                                                                    {/*UM Select*/}
                                                                                    <Select
                                                                                        type="text"
                                                                                        name="um"
                                                                                        value={price.um}
                                                                                        onChange={(e) => handleInputChange(index, e)}
                                                                                        className={`${BULK ? 'hidden' : ''} block w-full rounded-md ${darkMode ? `bg-darkMainColor` : `bg-white`} px-2 py-1.5 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                                                    >
                                                                                        <option value="">Select U/M...</option>
                                                                                        <option value="ACR">Acre</option>
                                                                                        <option value="B12">BAG 12#</option>
                                                                                        <option value="B22">BAG 22#</option>
                                                                                        <option value="B25">BAG 25#</option>
                                                                                        <option value="B30">BAG 30#</option>
                                                                                        <option value="B40">BAG 40#</option>
                                                                                        <option value="B5">BAG 5#</option>
                                                                                        <option value="B50">BAG 50#</option>
                                                                                        <option value="B51">BAG 51#</option>
                                                                                        <option value="BAG">BAG (Unspecified Weight)</option>
                                                                                        <option value="2.5">CASE (2x2.5)</option>
                                                                                        <option value="CS4">CASE (4X1)</option>
                                                                                        <option value="CAS">CASE</option>
                                                                                        <option value="30">DRUM 30-GAL</option>
                                                                                        <option value="55">DRUM 55-GAL</option>
                                                                                        <option value="DRU">DRUM (Unspecified Volume)</option>
                                                                                        <option value="EAC">EACH</option>
                                                                                        <option value="GAL">GALLON</option>
                                                                                        <option value="TR">TOTE REFILL</option>
                                                                                        <option value="LB">POUND</option>
                                                                                        <option value="OZ">Ounce - Av</option>
                                                                                        <option value="PAI">PAIL 5-GAL</option>
                                                                                        <option value="SAC">Super Sack (B1000)</option>
                                                                                        <option value="SAC2">Super Sack (B2000)</option>
                                                                                        <option value="TON">TON (2,000#)</option>
                                                                                        <option value="275">TOTE 275-GAL</option>
                                                                                    </Select>
                                                                                    
                                                                                   
                                                                                    {/*Rate Select */}
                                                                                    <Select
                                                                                        type="text"
                                                                                        name="um"
                                                                                        value={price.um}
                                                                                        onChange={(e) => handleInputChange(index, e)}
                                                                                        className={`${BULK ? '' : 'hidden'} block w-full rounded-md ${darkMode ? `bg-darkMainColor` : `bg-white`} px-2 py-1.5 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                                                    >
                                                                                        <option value="">Select Rate...</option>
                                                                                        <option value="0.25ACR">1/4 Ton Per Acre</option>
                                                                                        <option value="0.5ACR">1/2 Ton Per Acre</option>
                                                                                        <option value="0.75ACR">3/4 Ton Per Acre</option>
                                                                                        <option value="1ACR">1 Ton Per Acre</option>
                                                                                        <option value="1.25ACR">1 1/4 Ton Per Acre</option>
                                                                                        <option value="1.5ACR">1 1/2 Ton Per Acre</option>
                                                                                        <option value="2ACR">2 Ton Per Acre</option>
                                                                                    </Select>
                                                                                </div>
                                                                                
                                                                                {/*Product Code*/}
                                                                                <div className={`relative col-span-1`}>
                                                                                    <Input
                                                                                        name="code"
                                                                                        type="string"
                                                                                        placeholder="Code..."
                                                                                        value={price.code}
                                                                                        onChange={(e) => handleInputChange(index, e)}
                                                                                        className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                                                    />
                                                                                </div>
                                                                                
                                                                                {/*NE Price*/}
                                                                                <div className={`relative col-span-1`}>
                                                                                    
                                                                                    <div className={`flex w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}>
                                                                                        <div className="hidden lg:block text-borderColor text-xs mr-3">$</div>
                                                                                        <Input
                                                                                            type="number"
                                                                                            name="ne"
                                                                                            value={price.ne}
                                                                                            onChange={(e) => handleInputChange(index, e)}
                                                                                            placeholder={`NE`}
                                                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                                                        />
                                                                                        <div className="hidden lg:block text-borderColor w-fit text-xs ml-1">USD</div>
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                {/*SE Price*/}
                                                                                <div className={`relative col-span-1`}>
                                                                                    <div className={`flex w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}>
                                                                                        <div className="hidden lg:block text-borderColor text-xs mr-3">$</div>
                                
                                                                                        <Input
                                                                                            type="number"
                                                                                            name="se"
                                                                                            value={price.se}
                                                                                            onChange={(e) => handleInputChange(index, e)}
                                                                                            placeholder={`SE`}
                                                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                                                        />
                                                                                        <div className="hidden lg:block text-borderColor w-fit text-xs ml-1">USD</div>
                                
                                
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                {/*AG Price*/}
                                                                                <div className={`relative col-span-1`}>
                                                                                    <div className={`flex w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}>
                                                                                        <div className="hidden lg:block text-borderColor text-xs mr-3">$</div>
                                
                                                                                        <Input
                                                                                            type="number"
                                                                                            name="ag"
                                                                                            value={price.ag}
                                                                                            onChange={(e) => handleInputChange(index, e)}
                                                                                            placeholder={`AG`}
                                                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500
                                
                                
                                 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                                                        />
                                                                                        <div className="hidden lg:block text-borderColor w-fit text-xs ml-1">USD</div>
                                
                                
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                {/*DIST1 Price*/}
                                                                                <div className={`relative col-span-1`}>
                                                                                    <div className={`flex w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}>
                                                                                        <div className="hidden lg:block text-borderColor text-xs mr-3">$</div>
                                
                                                                                        <Input
                                                                                            type="number"
                                                                                            name="dist1"
                                                                                            value={price.dist1}
                                                                                            onChange={(e) => handleInputChange(index, e)}
                                                                                            placeholder={`DIST 1`}
                                                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500
                                
                                
                                 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                                                        />
                                                                                        <div className="hidden lg:block text-borderColor w-fit text-xs ml-1">USD</div>
                                
                                
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                {/*DIST2 Price*/}
                                                                                <div className={`relative col-span-1`}>
                                                                                    <div className={`flex w-full rounded-md ${darkMode ? 'bg-darkMainColor' : 'bg-white'} px-2 py-1 text-base placeholder:text-gray-500 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}>
                                                                                        <div className="hidden lg:block text-borderColor text-xs mr-3">$</div>
                                
                                                                                        <Input
                                                                                            type="number"
                                                                                            name="dist2"
                                                                                            value={price.dist2}
                                                                                            onChange={(e) => handleInputChange(index, e)}
                                                                                            placeholder={`DIST 2`}
                                                                                            className={`block w-full rounded-md ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-gray-900'} text-base placeholder:text-gray-500
                                
                                
                                 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-green-600 sm:text-sm/6`}
                                                                                        />
                                                                                        <div className="hidden lg:block text-borderColor w-fit text-xs ml-1">USD</div>
                                
                                
                                                                                    </div>
                                                                                </div>
                                                                                
                                                                            </div>
                                                                            
                                                                            <Button
                                                                                className="hover:scale-105 hover:animate-pulse text-red-500 w-fit h-fit mt-auto mb-auto rounded-full mr-1"
                                                                                onClick={() => handleRemovePrice(index)}
                                                                            >
                                                                                <XCircleIcon className="size-5"/>
                                                                            </Button>
                                        
                                                                        </div>
                                                                        
                                                                        
                                                                    </div>
                                                            
                                                                    
                                                                )
                                                            })}
                                                        </div>
                                    
                                                    )}
                                                </div>
                                
                                            </div>
                                        
                                            
                                            {/*Buttons*/}
                                            <div className="col-span-full mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                                <Button
                                                    type="submit"
                                                    className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-opacity-50 sm:ml-3 sm:w-auto"
                                                >
                                                    Add Product
                                                </Button>
                                                <Button
                                                    type="button"
                                                    data-autofocus
                                                    onClick={() => setAdding(false)}
                                                    className={`mt-3 inline-flex w-full justify-center rounded-md ${darkMode ? 'bg-darkBgColor' : 'bg-white ring-1 ring-inset ring-gray-300'} px-3 py-2 text-sm font-semibold shadow-sm hover:bg-opacity-50 sm:mt-0 sm:w-auto`}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                            
            
                                    </form>
                                    
                                </div>
                            </div>
                            
                        
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>

            {/*Upload & Update*/}
            <Dialog open={upload} onClose={setUpload} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
              />
        
              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <DialogPanel
                    transition
                    className={`relative transform overflow-hidden rounded-lg ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-6xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                  >
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-green-600/15 sm:mx-0 sm:size-10">
                        <ArrowUpOnSquareIcon aria-hidden="true" className="size-6 text-green-600" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <DialogTitle as="h3" className="text-base font-semibold">
                          Upload Pricing Export
                        </DialogTitle>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            You can upload the data export from Infor into here and the sales tool will automatically update itself!
                          </p>
                            
                            
                            {/* Upload File */}
                              <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} />
                        
                              {/* Show info after upload */}
                              {ready && (
                                <div className="space-y-2">
                                  <p>Total Items in Upload: {progress.total}</p>
                                  <button
                                    onClick={handleCompareAndUpload}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg"
                                  >
                                    Start Compare & Upload
                                  </button>
                                </div>
                              )}
                        
                              {/*/!* Progress *!/*/}
                              {/*{progress.done > 0 && (*/}
                              {/*  <p>*/}
                              {/*    Uploaded {progress.done} / {progress.total} items*/}
                              {/*  </p>*/}
                              {/*)}*/}
                            
                            {progress.total > 0 && (
        <div className="mt-4">
          <div className="text-sm mb-1">
            Processing {progress.done} of {progress.total} rows
          </div>
          <div className="w-full bg-gray-300 rounded h-4">
            <div
              className="bg-green-500 h-4 rounded"
              style={{
                width: `${(progress.done / progress.total) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      )}
                            
                            
                             {/* Preview Changes Modal/Section */}
      {showPreview && (
        <div className="mt-4 p-4 bg-green-600/10 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Preview Changes</h2>

          <div className="max-h-64 overflow-y-auto text-xs space-y-2">
            {comparisonResults.length === 0 ? (
              <p>No changes detected.</p>
            ) : (
              comparisonResults.map((item, idx) => (
                <div key={idx} className="border p-2 rounded bg-white/5">
                  <div className="font-semibold">
                    {item.productName} ({item.code} - {item.um})
                  </div>

                  {Object.entries(item.changes).map(([key, val]) => (
                    <div key={key} className="flex gap-x-2">
                      <span className="text-gray-400">{key.toUpperCase()}:</span>
                      <span className="text-red-400">{val.old}</span>
                      <span>→</span>
                      <span className="text-green-400">{val.new}</span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>

          <div className="mt-3 flex gap-x-2">
            <button
              onClick={handleConfirmUpload}
              className="bg-green-600 text-white px-3 py-1 rounded"
            >
              Confirm Upload
            </button>

            <button
              onClick={() => setShowPreview(false)}
              className="bg-gray-500 text-white px-3 py-1 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
                        
                        
                        
                        
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <Button
                        type="button"
                        // onClick={() => handleDeleteOrderPlanned(order.customerUID, order.id, order.salesUID)}
                        className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      >
                        Update
                      </Button>
                      <Button
                        type="button"
                        data-autofocus
                        onClick={() => setUpload(false)}
                        className={`mt-3 inline-flex w-full justify-center rounded-md ${darkMode ? 'bg-darkBgColor' : 'bg-white ring-1 ring-inset ring-gray-300'} px-3 py-2 text-sm font-semibold shadow-sm hover:bg-opacity-50 sm:mt-0 sm:w-auto`}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DialogPanel>
                </div>
              </div>
            </Dialog>



        </div>
    )
};


export default Pricing;
